import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Download, Loader2, Warehouse as WarehouseIcon, AlertTriangle, Plus, ArrowLeftRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof detail?.message === 'string' && detail.message.trim()) return detail.message;
  return fallback;
};

export default function WarehouseStockDetailsPage() {
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [variants, setVariants] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [stock, setStock] = useState([]);

  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    variant_id: '',
    qty: '',
    note: '',
    batch_no: '',
  });
  const [transferForm, setTransferForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    variant_id: '',
    qty: '',
    note: '',
    batch_no: '',
  });
  const [transferRawConsumptions, setTransferRawConsumptions] = useState([{ raw_item_id: '', kg_required: '', note: '' }]);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  const total = stock.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const sortedStock = useMemo(() => (
    [...stock].sort((a, b) => {
      const productCompare = String(a.master_name || '').localeCompare(String(b.master_name || ''), 'en', { sensitivity: 'base' });
      if (productCompare !== 0) return productCompare;
      const packCompare = String(a.pack_size_label || '').localeCompare(String(b.pack_size_label || ''), 'en', { sensitivity: 'base' });
      if (packCompare !== 0) return packCompare;
      return String(a.sku || '').localeCompare(String(b.sku || ''), 'en', { sensitivity: 'base' });
    })
  ), [stock]);

  const pagedStock = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedStock.slice(start, start + pageSize);
  }, [sortedStock, page]);

  const variantOptions = useMemo(() => variants.filter((variant) => variant.is_active), [variants]);
  const rawItemOptions = useMemo(() => rawItems.filter((item) => item.is_active !== false), [rawItems]);

  const resetAdjustForm = () => {
    setAdjustForm({
      variant_id: '',
      qty: '',
      note: '',
      batch_no: '',
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      from_warehouse_id: selectedWarehouseId || '',
      to_warehouse_id: '',
      variant_id: '',
      qty: '',
      note: '',
      batch_no: '',
    });
    setTransferRawConsumptions([{ raw_item_id: '', kg_required: '', note: '' }]);
  };

  const addTransferRawConsumptionRow = () => {
    setTransferRawConsumptions((prev) => [...prev, { raw_item_id: '', kg_required: '', note: '' }]);
  };

  const removeTransferRawConsumptionRow = (index) => {
    setTransferRawConsumptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const isPackingToSalesTransfer = useMemo(() => {
    const fromWh = warehouses.find((warehouse) => warehouse.id === transferForm.from_warehouse_id);
    const toWh = warehouses.find((warehouse) => warehouse.id === transferForm.to_warehouse_id);
    if (!fromWh || !toWh) return false;
    const fromName = (fromWh.name || '').toLowerCase();
    const toName = (toWh.name || '').toLowerCase();
    return fromName.includes('packing') && (Boolean(toWh.is_default_sales) || toName.includes('sales'));
  }, [warehouses, transferForm.from_warehouse_id, transferForm.to_warehouse_id]);

  const fetchStock = async (warehouseId) => {
    setStockLoading(true);
    try {
      const response = await adminApi.getWarehouseStock(warehouseId);
      setStock(response.data || []);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load warehouse stock');
    } finally {
      setStockLoading(false);
    }
  };

  const handleAdjustStock = async () => {
    if (!selectedWarehouseId) {
      toast.error('Select a warehouse first');
      return;
    }
    if (!adjustForm.variant_id) {
      toast.error('Select a product variant');
      return;
    }
    if (!adjustForm.qty) {
      toast.error('Enter quantity');
      return;
    }

    setSaving(true);
    try {
      await adminApi.adjustWarehouseStock(selectedWarehouseId, {
        items: [
          {
            variant_id: adjustForm.variant_id,
            qty: parseFloat(adjustForm.qty),
            note: adjustForm.note || undefined,
            batch_no: adjustForm.batch_no || undefined,
          },
        ],
      });
      toast.success('Stock adjusted');
      setShowAdjustModal(false);
      resetAdjustForm();
      fetchStock(selectedWarehouseId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to adjust stock'));
    } finally {
      setSaving(false);
    }
  };

  const handleTransferStock = async () => {
    if (!transferForm.from_warehouse_id || !transferForm.to_warehouse_id) {
      toast.error('Select source and destination');
      return;
    }
    if (transferForm.from_warehouse_id === transferForm.to_warehouse_id) {
      toast.error('Source and destination must be different');
      return;
    }
    if (!transferForm.variant_id) {
      toast.error('Select a product variant');
      return;
    }
    if (!transferForm.qty) {
      toast.error('Enter quantity');
      return;
    }

    const preparedRawConsumptions = transferRawConsumptions
      .filter((row) => row.raw_item_id && row.kg_required)
      .map((row) => ({
        raw_item_id: row.raw_item_id,
        kg_required: parseFloat(row.kg_required),
        note: row.note?.trim() || undefined,
      }))
      .filter((row) => Number.isFinite(row.kg_required) && row.kg_required > 0);

    if (isPackingToSalesTransfer && preparedRawConsumptions.length === 0) {
      toast.error('Add raw material consumption for Packing → Sales transfer');
      return;
    }

    setSaving(true);
    try {
      await adminApi.transferStock({
        from_warehouse_id: transferForm.from_warehouse_id,
        to_warehouse_id: transferForm.to_warehouse_id,
        items: [
          {
            variant_id: transferForm.variant_id,
            qty: parseFloat(transferForm.qty),
            note: transferForm.note || undefined,
            batch_no: transferForm.batch_no || undefined,
          },
        ],
        note: transferForm.note || undefined,
        raw_consumptions: preparedRawConsumptions.length > 0 ? preparedRawConsumptions : undefined,
      });
      toast.success('Stock transferred');
      setShowTransferModal(false);
      resetTransferForm();
      fetchStock(selectedWarehouseId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to transfer stock'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const [warehouseRes, variantsRes, rawItemsRes] = await Promise.all([
          adminApi.getWarehouses(),
          adminApi.getProductVariants(),
          adminApi.getRawItems(),
        ]);
        const warehouseList = warehouseRes.data || [];
        setWarehouses(warehouseList);
        setVariants(variantsRes.data || []);
        setRawItems(rawItemsRes.data || []);
        if (warehouseList.length > 0) {
          const defaultWarehouse = warehouseList.find((w) => w.is_default_sales);
          setSelectedWarehouseId(defaultWarehouse?.id || warehouseList[0].id);
        }
      } catch (error) {
        toast.error('Failed to load warehouses');
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, []);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    fetchStock(selectedWarehouseId);
  }, [selectedWarehouseId]);

  const handleDownloadExcel = async () => {
    if (!selectedWarehouseId) {
      toast.error('Select a warehouse first');
      return;
    }
    try {
      const res = await adminApi.downloadAdminDatasetExcel('warehouse-stock', { warehouse_id: selectedWarehouseId });
      downloadBlob(res.data, `warehouse-stock.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  return (
    <div className="space-y-6" data-testid="warehouse-stock-details-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Stock Details</h1>
        <p className="text-[#A8A8A8] mt-1">Warehouse-wise finished goods stock</p>
      </div>
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleDownloadExcel}>
          <Download className="w-4 h-4 mr-2" /> Download Excel
        </Button>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="w-full lg:w-80">
              <Label>Warehouse</Label>
              <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => { resetAdjustForm(); setShowAdjustModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Adjust Stock
              </Button>
              <Button variant="outline" onClick={() => { resetTransferForm(); setShowTransferModal(true); }}>
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Stock Transfer
              </Button>
            </div>
          </div>

          {loading || stockLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : stock.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <WarehouseIcon className="w-6 h-6 mr-2" />
              No stock records yet
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">SKU</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">On Hand</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Available</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {pagedStock.map((item) => (
                      <tr key={item.variant_id} className="hover:bg-[#FAFAF9]/60">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-[#2D2420]">{item.master_name}</p>
                            <p className="text-sm text-[#A8A8A8]">{item.pack_size_label}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#A8A8A8]">{item.sku}</td>
                        <td className={cn('py-3 px-4 text-right font-semibold', item.is_negative ? 'text-[#E76F51]' : 'text-[#2D2420]')}>{item.on_hand}</td>
                        <td className="py-3 px-4 text-right text-[#2D2420]">{item.available}</td>
                        <td className="py-3 px-4 text-right">
                          {item.is_negative ? (
                            <Badge className="bg-red-100 text-red-800 text-xs inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Negative
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 text-xs">OK</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                <p className="text-sm text-[#A8A8A8]">
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.max(1, prev - 1));
                        }}
                        className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-3 text-sm text-[#5C4033]">Page {page} of {totalPages}</span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.min(totalPages, prev + 1));
                        }}
                        className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Adjust Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Variant</Label>
              <Select value={adjustForm.variant_id} onValueChange={(value) => setAdjustForm((prev) => ({ ...prev, variant_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  {variantOptions.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.master_name} - {variant.pack_size_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity (+/-)</Label>
              <Input
                type="number"
                value={adjustForm.qty}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, qty: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Batch Number</Label>
              <Input
                value={adjustForm.batch_no}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, batch_no: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={adjustForm.note}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Transfer Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Warehouse</Label>
              <Select value={transferForm.from_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, from_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Warehouse</Label>
              <Select value={transferForm.to_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, to_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Variant</Label>
              <Select value={transferForm.variant_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, variant_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  {variantOptions.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.master_name} - {variant.pack_size_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={transferForm.qty}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, qty: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Batch Number</Label>
              <Input
                value={transferForm.batch_no}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, batch_no: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={transferForm.note}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>

            {isPackingToSalesTransfer && (
              <div className="border border-[#E5E5E5] rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#2D2420]">Raw Material Consumption</p>
                    <p className="text-xs text-[#A8A8A8]">Required for Packing → Sales transfer</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addTransferRawConsumptionRow}>
                    <Plus className="w-4 h-4 mr-1" /> Add Row
                  </Button>
                </div>

                {transferRawConsumptions.map((row, index) => (
                  <div key={`raw-consumption-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-5">
                      <Label>Raw Material</Label>
                      <Select
                        value={row.raw_item_id}
                        onValueChange={(value) => {
                          setTransferRawConsumptions((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], raw_item_id: value };
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger className="border-[#E5E5E5]">
                          <SelectValue placeholder="Select raw material" />
                        </SelectTrigger>
                        <SelectContent>
                          {rawItemOptions.map((item) => (
                            <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label>KG Used</Label>
                      <Input
                        type="number"
                        step="0.001"
                        value={row.kg_required}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTransferRawConsumptions((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], kg_required: value };
                            return next;
                          });
                        }}
                        className="border-[#E5E5E5]"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Label>Note</Label>
                      <Input
                        value={row.note}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTransferRawConsumptions((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], note: value };
                            return next;
                          });
                        }}
                        className="border-[#E5E5E5]"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTransferRawConsumptionRow(index)}
                        disabled={transferRawConsumptions.length === 1}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button onClick={handleTransferStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
